import { ContextMenu as KobalteContextMenu } from "@kobalte/core/context-menu";
import {
  createEffect,
  createSignal,
  For,
  JSX,
  onCleanup,
  Show,
  splitProps,
  type ValidComponent,
} from "solid-js";
import { Portal } from "solid-js/web";
import { Icon, type IconName } from "../icons/registry";
import { currentMenuViewport, dropdownPosition } from "./dropdownPosition";

export type MenuAction =
  | {
      type: "separator";
    }
  | {
      label: string;
      icon?: IconName;
      onClick: () => void;
      disabled?: boolean;
      destructive?: boolean;
      /** small pill shown after the label (e.g. "offline") - purely
       * informational, never affects onClick/disabled. */
      badge?: string;
      type?: never;
    };

// fitViewport sizes the positioner, not its scrollable menu content.
const viewportMenuStyle: JSX.CSSProperties = {
  "min-width": "min(12rem, var(--kb-popper-content-available-width, calc(100vw - 16px)))",
  "max-width": "var(--kb-popper-content-available-width, calc(100vw - 16px))",
  "max-height": "var(--kb-popper-content-available-height, calc(100dvh - 16px))",
};

// type guard to check if action is not a separator
function isActionItem(action: MenuAction): action is Exclude<MenuAction, { type: "separator" }> {
  return action.type !== "separator";
}

export interface ContextMenuProps {
  /** trigger element - the element that will show the menu on right-click */
  children: JSX.Element;
  /** menu actions to display */
  actions: MenuAction[];
  /** additional content to show at the top of the menu */
  header?: JSX.Element;
  /** callback when menu opens */
  onOpen?: () => void;
  /** polymorphic element for the trigger (defaults to kobalte's default).
   *  use e.g. `as="tr"` when the trigger needs to be a specific element
   *  for layout reasons (e.g. table rows). */
  as?: ValidComponent;
  /** extra classes merged onto the trigger element */
  triggerClass?: string;
  /** overrides the default z-[1200] flyout tier - use sparingly, only for
   *  a menu that must out-rank z-1200's usual siblings (e.g. the title
   *  bar's global chrome menu, which should sit above page content). */
  zIndex?: number;
}

// context menu component using kobalte primitives
// automatically handles right-click, positioning, and viewport constraints
export function ContextMenu(props: ContextMenuProps) {
  const [local, rest] = splitProps(props, [
    "children",
    "actions",
    "header",
    "onOpen",
    "as",
    "triggerClass",
    "zIndex",
  ]);

  const handleOpenChange = (open: boolean) => {
    if (open && local.onOpen) {
      local.onOpen();
    }
  };

  return (
    <KobalteContextMenu
      onOpenChange={handleOpenChange}
      fitViewport
      overlap
      overflowPadding={8}
      {...rest}
    >
      <KobalteContextMenu.Trigger
        as={local.as as ValidComponent}
        class={`outline-none ${local.triggerClass ?? ""}`}
      >
        {local.children}
      </KobalteContextMenu.Trigger>

      <KobalteContextMenu.Portal>
        <KobalteContextMenu.Content
          class="
            bg-[var(--color-bg-elevated)]
            border border-[var(--color-border-default)]
            rounded-lg
            shadow-2xl
            overflow-y-auto overflow-x-hidden
            animate-[fade-in_0.15s_ease-out]
            origin-top-left
            data-[expanded]:animate-[fade-in_0.15s_ease-out]
          "
          style={{ ...viewportMenuStyle, "z-index": local.zIndex ?? 1200 }}
        >
          <Show when={local.header}>
            <div class="p-2 border-b border-[var(--color-border-default)]">{local.header}</div>
          </Show>

          <div class="py-1">
            <For each={local.actions}>
              {(action) => {
                if (!isActionItem(action)) {
                  return (
                    <KobalteContextMenu.Separator class="my-1 h-px bg-[var(--color-border-subtle)]" />
                  );
                }

                return (
                  <KobalteContextMenu.Item
                    class={`
                      w-full px-4 py-2 text-left flex items-center gap-3
                      transition-colors body-small outline-none cursor-pointer
                      ${
                        action.disabled
                          ? "text-[var(--color-text-disabled)] cursor-not-allowed opacity-50"
                          : action.destructive
                            ? "text-[var(--color-error)] data-[highlighted]:bg-[var(--color-error)] data-[highlighted]:text-white"
                            : "text-[var(--color-text-primary)] data-[highlighted]:bg-[var(--color-bg-hover)]"
                      }
                    `}
                    onSelect={() => !action.disabled && action.onClick()}
                    disabled={action.disabled}
                    closeOnSelect={true}
                  >
                    <Show when={action.icon}>
                      <Icon name={action.icon!} size={16} color="currentColor" />
                    </Show>
                    <span class="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">{action.label}</span>
                    <Show when={action.badge}>
                      <span class="shrink-0 rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                        {action.badge}
                      </span>
                    </Show>
                  </KobalteContextMenu.Item>
                );
              }}
            </For>
          </div>
        </KobalteContextMenu.Content>
      </KobalteContextMenu.Portal>
    </KobalteContextMenu>
  );
}

// dropdown menu component (same as context menu but triggered by click instead of right-click)
export interface DropdownMenuProps {
  /** trigger element - the button/element that opens the menu */
  trigger: JSX.Element;
  /** menu actions to display */
  actions: MenuAction[];
  /** additional content to show at the top of the menu */
  header?: JSX.Element;
  /** called each time the menu opens - e.g. to refresh data the actions
   * depend on right before they're shown */
  onOpen?: () => void;
  /** which side of the trigger the menu's edge anchors to (only used by
   * `ClickDropdownMenu`). defaults to "right", which anchors the menu's
   * right edge to the trigger's right edge - wrong for a trigger near the
   * left edge of the viewport (e.g. the title-bar hamburger), where it
   * pushes the menu off-screen to the left. use "left" there instead. */
  align?: "left" | "right";
  /** overrides the default z-[1200] flyout tier - see `ContextMenuProps`'s
   *  own doc comment for the same field. */
  zIndex?: number;
}

export function DropdownMenu(props: DropdownMenuProps) {
  const [local, rest] = splitProps(props, ["trigger", "actions", "header", "onOpen"]);

  return (
    <KobalteContextMenu fitViewport overlap overflowPadding={8} {...rest}>
      <KobalteContextMenu.Trigger class="outline-none">{local.trigger}</KobalteContextMenu.Trigger>

      <KobalteContextMenu.Portal>
        <KobalteContextMenu.Content
          class="
            bg-[var(--color-bg-elevated)]
            border border-[var(--color-border-default)]
            rounded-lg
            shadow-2xl
            overflow-y-auto overflow-x-hidden
            animate-[fade-in_0.15s_ease-out]
            z-[1200]
            origin-top-left
            data-[expanded]:animate-[fade-in_0.15s_ease-out]
          "
          style={viewportMenuStyle}
        >
          <Show when={local.header}>
            <div class="p-2 border-b border-[var(--color-border-default)]">{local.header}</div>
          </Show>

          <div class="py-1">
            <For each={local.actions}>
              {(action) => {
                if (!isActionItem(action)) {
                  return (
                    <KobalteContextMenu.Separator class="my-1 h-px bg-[var(--color-border-subtle)]" />
                  );
                }

                return (
                  <KobalteContextMenu.Item
                    class={`
                      w-full px-4 py-2 text-left flex items-center gap-3
                      transition-colors body-small outline-none cursor-pointer
                      ${
                        action.disabled
                          ? "text-[var(--color-text-disabled)] cursor-not-allowed opacity-50"
                          : action.destructive
                            ? "text-[var(--color-error)] data-[highlighted]:bg-[var(--color-error)] data-[highlighted]:text-white"
                            : "text-[var(--color-text-primary)] data-[highlighted]:bg-[var(--color-bg-hover)]"
                      }
                    `}
                    onSelect={() => !action.disabled && action.onClick()}
                    disabled={action.disabled}
                    closeOnSelect={true}
                  >
                    <Show when={action.icon}>
                      <Icon name={action.icon!} size={16} color="currentColor" />
                    </Show>
                    <span class="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">{action.label}</span>
                    <Show when={action.badge}>
                      <span class="shrink-0 rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                        {action.badge}
                      </span>
                    </Show>
                  </KobalteContextMenu.Item>
                );
              }}
            </For>
          </div>
        </KobalteContextMenu.Content>
      </KobalteContextMenu.Portal>
    </KobalteContextMenu>
  );
}

// click-triggered dropdown menu using position:fixed portal positioning.
// bypasses @floating-ui entirely (kobalte's dropdown uses strategy:"absolute"
// which can miscompute available space when inside a scrolled container, causing
// the menu to clip at viewport edges). uses getBoundingClientRect + position:fixed
// for reliable viewport-relative placement regardless of ancestor transforms or
// overflow contexts.
export function ClickDropdownMenu(props: DropdownMenuProps) {
  const [local] = splitProps(props, ["trigger", "actions", "header", "onOpen", "align", "zIndex"]);

  const [open, setOpen] = createSignal(false);
  const [menuPos, setMenuPos] = createSignal<JSX.CSSProperties>({});
  let triggerRef: HTMLDivElement | undefined;
  let menuRef: HTMLDivElement | undefined;

  const computePos = () =>
    dropdownPosition(triggerRef!.getBoundingClientRect(), currentMenuViewport(), local.align);

  const openMenu = (e: MouseEvent) => {
    e.stopPropagation();
    if (!triggerRef) return;
    if (open()) {
      setOpen(false);
      return;
    }
    setMenuPos(computePos());
    setOpen(true);
    local.onOpen?.();
  };

  const closeMenu = () => setOpen(false);

  createEffect(() => {
    if (open()) {
      const onPointerDown = (e: PointerEvent) => {
        if (!menuRef?.contains(e.target as Node) && !triggerRef?.contains(e.target as Node)) {
          closeMenu();
        }
      };
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeMenu();
      };
      document.addEventListener("pointerdown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      const reposition = () => setMenuPos(computePos());
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
      window.visualViewport?.addEventListener("resize", reposition);
      window.visualViewport?.addEventListener("scroll", reposition);
      onCleanup(() => {
        document.removeEventListener("pointerdown", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("resize", reposition);
        window.removeEventListener("scroll", reposition, true);
        window.visualViewport?.removeEventListener("resize", reposition);
        window.visualViewport?.removeEventListener("scroll", reposition);
      });
    }
  });

  const menuStyle = (): JSX.CSSProperties => {
    return {
      ...menuPos(),
      "z-index": `${local.zIndex ?? 1200}`,
    };
  };

  const itemClass = (action: Exclude<MenuAction, { type: "separator" }>) =>
    `w-full px-4 py-2 text-left flex items-center gap-3 transition-colors body-small outline-none cursor-pointer
    ${
      action.disabled
        ? "text-[var(--color-text-disabled)] cursor-not-allowed opacity-50"
        : action.destructive
          ? "text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white focus:bg-[var(--color-error)] focus:text-white"
          : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] focus:bg-[var(--color-bg-hover)]"
    }`;

  return (
    <>
      <div ref={(el) => (triggerRef = el)} class="outline-none inline-flex" onClick={openMenu}>
        {local.trigger}
      </div>
      <Show when={open()}>
        <Portal mount={document.body}>
          <div
            ref={(el) => (menuRef = el)}
            role="menu"
            style={menuStyle()}
            class="bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] rounded-lg shadow-2xl overflow-y-auto overflow-x-hidden animate-[fade-in_0.15s_ease-out]"
          >
            <Show when={local.header}>
              <div class="p-2 border-b border-[var(--color-border-default)]">{local.header}</div>
            </Show>
            <div class="py-1">
              <For each={local.actions}>
                {(action) => {
                  if (!isActionItem(action)) {
                    return (
                      <div role="separator" class="my-1 h-px bg-[var(--color-border-subtle)]" />
                    );
                  }
                  return (
                    <button
                      role="menuitem"
                      class={itemClass(action)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!action.disabled) {
                          action.onClick();
                          closeMenu();
                        }
                      }}
                      disabled={action.disabled}
                    >
                      <Show when={action.icon}>
                        <Icon name={action.icon!} size={16} color="currentColor" />
                      </Show>
                      <span class="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">
                        {action.label}
                      </span>
                      <Show when={action.badge}>
                        <span class="shrink-0 rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                          {action.badge}
                        </span>
                      </Show>
                    </button>
                  );
                }}
              </For>
            </div>
          </div>
        </Portal>
      </Show>
    </>
  );
}
